import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Heart, Mail, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        source: 'waitlist'
      });

      if (response.data.status === 'success' || response.data.status === 'already_subscribed') {
        setIsSubscribed(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F5F8] via-[#ECE8EF] to-[#E7C6C2] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center gap-3 mb-4">
            <Heart className="w-12 h-12 text-[#6C4CA2]" fill="#6C4CA2" />
            <h1 className="text-5xl sm:text-6xl font-bold text-[#3E324D]">
              SheSafe Haven
            </h1>
          </div>
          <p className="text-xl text-[#6E6275] max-w-xl mx-auto">
            Your gentle space for healing begins here
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 mb-8">
          {!isSubscribed ? (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[#3E324D] mb-4">
                  Get Daily Whispers of Healing
                </h2>
                <p className="text-[#6E6275] leading-relaxed mb-2">
                  Join thousands of women who start their day with gentle reminders that they're not alone.
                </p>
                <p className="text-[#6E6275] leading-relaxed">
                  Receive daily whispers of strength, healing, and sisterhood — delivered with love from Angie.
                </p>
              </div>

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[#3E324D] font-medium mb-2">
                    Your email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6E6275]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-12 pr-4 py-4 border-2 border-[#E7C6C2] rounded-xl focus:outline-none focus:border-[#6C4CA2] transition-colors text-[#3E324D]"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white py-6 text-lg rounded-xl disabled:opacity-50"
                >
                  {isSubmitting ? 'Joining...' : '💜 Join the Waitlist'}
                </Button>
              </form>

              <p className="text-xs text-[#6E6275] text-center mt-4">
                We respect your privacy. Unsubscribe anytime. No spam, just healing.
              </p>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-[#7C9069]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-[#7C9069]" />
              </div>
              <h2 className="text-3xl font-bold text-[#3E324D] mb-4">
                Welcome to the Family! 💜
              </h2>
              <p className="text-[#6E6275] leading-relaxed mb-6">
                You're all set to receive daily whispers. Check your inbox soon for your first message.
              </p>
              <Button
                onClick={() => navigate('/signup')}
                className="bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] px-8 py-4 rounded-full"
              >
                Create Your Account
              </Button>
            </div>
          )}
        </div>

        {/* CTA to App */}
        <div className="text-center">
          <p className="text-[#6E6275] mb-4">Already have an account?</p>
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="text-[#6C4CA2] hover:text-[#8B6BB8]"
          >
            Sign In to Your Haven →
          </Button>
        </div>

        {/* About Section */}
        <div className="mt-12 text-center">
          <p className="text-[#6E6275] italic max-w-xl mx-auto leading-relaxed">
            "Whether you're healing from divorce, navigating single motherhood, or rediscovering yourself after heartbreak — this space was built for you, with love and intention."
          </p>
          <p className="signature-text text-3xl text-[#6C4CA2] mt-4">
            Angie
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaitlistPage;
